import * as React from "react";
import { getToken, linksOf, request } from "../../../services/cpln";
import { fetchGvcLocations } from "../../../mst/kinds/gvc.helpers";
import { Terminal } from "xterm";
import { Alert, Button, Input, notification, Select, Space, Tooltip } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { useDebounce } from "../../../components/table/useDebounce";
import { v4 as uuidv4 } from "uuid";
import { ChevronDown, ChevronUp, Loader } from "react-feather";
import { CodeSnippet } from "../../../components/generic/codeSnippet/codeSnippet";
import { Buffer } from "buffer";

interface SocketMessage {
  type: string;
  buffer: number[];
  width: number;
  height: number;
}
import { DOCS_URL, REQUEST_REPLICAS_INTERVAL_MS } from "../../../envVariables";
import { useSearchParams } from "react-router-dom";
import { Workload } from "../../../schema/types/workload/entity";
import { useAppSelector } from "../../../store/hooks";
import { selectContext } from "../../../store/contextSlice";
import { parseLink } from "../../../utils/linkParser/linkParser";

// Global settings for collapsible UI state (shared across all workloads)
const STORAGE_KEY_SHOW_PORT_FORWARD_COMMAND = "show-workload-port-forward-command";
const STORAGE_KEY_SHOW_CONNECT_COMMAND = "show-workload-connect-command";

// Per-workload connect command storage using absolute link
const GET_STORAGE_KEY_CONNECT_COMMAND = (selfLink: string) => `connect-command-${selfLink}`;

interface Props {
  workload: Workload;
}
export const WorkloadConnect: React.FC<Props> = ({ workload }) => {
  const { org } = useAppSelector(selectContext);
  const selfLink = linksOf(workload).self!;
  const parsedSelfLink = parseLink(selfLink, { useInputCtx: true });
  const gvc = parsedSelfLink.gvc;

  const [isLoading, setIsLoading] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [remoteHost, setRemoteHost] = React.useState("");

  const [gvcLocations, setGVCLocations] = React.useState<string[]>([]);
  const [locationFetchFailed, setLocationFetchFailed] = React.useState(false);
  const [replicas, setReplicas] = React.useState<string[]>([]);
  const [counter, _setCounter] = React.useState(0);
  const [command, setCommand] = React.useState(
    localStorage.getItem(GET_STORAGE_KEY_CONNECT_COMMAND(selfLink)) || "bash",
  );

  const [disconnected, setDisconnected] = React.useState(false);
  const [isTerminalOpen, setIsTerminalOpen] = React.useState(false);
  const socketRef = React.useRef<WebSocket | null>(null);
  const [debouncedCounter, setDebouncedCounter] = useDebounce("1", 250);
  const [isConnectHelpOpen, setIsConnectHelpOpen] = React.useState(true);
  const [isPortForwardHelpOpen, setIsPortForwardHelpOpen] = React.useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  let autoConnect = searchParams.get("autoConnect")?.toString() === "true";
  let location = searchParams.get("location");
  let container = searchParams.get("container");
  let paramReplica = searchParams.get("replica");

  const [locationInput, setLocationInput] = React.useState("");

  const [containerInput, setContainerInput] = React.useState((workload.spec.containers || []).map((c) => c.name)[0]);
  const containerOptions = (workload.spec.containers || [])
    .map((c) => c.name)
    .map((container) => ({ label: container, value: container }));

  const [replica, setReplica] = React.useState("");
  const [lastUsedReplica, setLastUsedReplica] = React.useState("");

  const terminalRef = React.useRef<Terminal>(getTerminal());

  React.useEffect(() => {
    fetchGVCLocations();

    const resizeObserver = new ResizeObserver(() => {
      incrementDebouncedCounter();
    });

    const terminalParent = document.getElementById("terminal-parent");
    if (terminalParent) {
      resizeObserver.observe(terminalParent);
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }

      if (terminalParent) {
        resizeObserver.unobserve(terminalParent);
      }
    };
  }, []);

  React.useEffect(() => {
    const _value = localStorage.getItem(STORAGE_KEY_SHOW_CONNECT_COMMAND);
    if (_value === null) {
      return;
    }
    setIsConnectHelpOpen(_value === "true");
  }, []);

  React.useEffect(() => {
    const _value = localStorage.getItem(STORAGE_KEY_SHOW_PORT_FORWARD_COMMAND);
    if (_value === null) {
      return;
    }
    setIsPortForwardHelpOpen(_value === "true");
  }, []);

  React.useEffect(() => {
    localStorage.setItem(GET_STORAGE_KEY_CONNECT_COMMAND(selfLink), command);
  }, [command]);

  React.useEffect(() => {
    if (gvcLocations.length < 1) {
      return;
    }
    if (location && gvcLocations.includes(location as string)) {
      setLocationInput(location as string);
      if (container && (workload.spec.containers || []).map((c) => c.name).includes(container as string)) {
        setContainerInput(container as string);
      }
    } else {
      setLocationInput(gvcLocations[0]);
    }
    setSearchParams((params) => {
      params.delete("location");
      params.delete("container");
      return params;
    });
  }, [gvcLocations]);

  const locationOptions = React.useMemo(() => {
    if (gvcLocations.length < 1) {
      return [
        { label: "Select a Location", value: "select" },
        ...gvcLocations.map((loc) => ({ value: loc, label: loc })),
      ];
    }
    return gvcLocations.map((loc) => ({ value: loc, label: loc }));
  }, [gvcLocations]);

  React.useEffect(() => {
    const loc = locationInput;
    if (loc === "select") {
      return;
    }
    fetchRemoteHost();
  }, [locationInput]);

  React.useEffect(() => {
    fit();
  }, [debouncedCounter]);

  function incrementDebouncedCounter() {
    setDebouncedCounter((_d) => String(Number(_d) + 1));
  }

  async function fetchRemoteHost() {
    if (!locationInput) {
      return;
    }
    try {
      setIsLoading(true);
      const deploymentLink = `${selfLink}/deployment/${locationInput}`;
      const { data: deployment } = await request({ url: deploymentLink });
      setRemoteHost(deployment.status.remote);
      setIsLoading(false);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to fetch clusterId of deployment" });
      setRemoteHost("");
      setIsLoading(false);
    }
  }

  React.useEffect(() => {
    if (!remoteHost) {
      setReplicas([]);
      return;
    }
    fetchReplicas();
  }, [remoteHost]);

  React.useEffect(() => {
    if (!remoteHost || socketRef.current) {
      return;
    }
    const intervalId = setInterval(() => fetchReplicas(true), REQUEST_REPLICAS_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [remoteHost, counter]);

  React.useEffect(() => {
    if (replicas.length > 0) {
      if (paramReplica) {
        if (replicas.includes(paramReplica)) {
          setReplica(paramReplica);
        }
        setSearchParams((params) => {
          params.delete("replica");
          return params;
        });
      }
    }
  }, [replicas]);

  React.useEffect(() => {
    if (replicas.length < 1) {
      return;
    }
    if ((!replica && !paramReplica) || !!replica) {
      if (autoConnect) {
        onConnect();
        setSearchParams((params) => {
          params.delete("autoConnect");
          return params;
        });
      }
    }
  }, [replicas, replica]);

  function setCounter() {
    _setCounter((x) => x + 1);
  }

  function getTerminal(): Terminal {
    return new Terminal({ cursorBlink: true, cursorStyle: "underline", cursorWidth: 5 });
  }

  async function fetchGVCLocations() {
    try {
      setIsLoading(true);
      const result = await fetchGvcLocations(gvc!);
      setGVCLocations(result.enabled);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      setLocationFetchFailed(true);
      notification.warning({ message: "Failed to fetch gvc locations" });
    }
  }

  async function fetchReplicas(silent = false) {
    try {
      if (!silent) setIsLoading(true);
      const url = `${remoteHost}/replicas/org/${org}/gvc/${gvc}/workload/${workload.name}`;
      const res = await request({ service: "self", url: url });
      setReplicas(res.data.items || []);
      if (!silent) setIsLoading(false);
    } catch (e) {
      if (!silent) {
        setIsLoading(false);
        notification.warning({
          message: "Failed to fetch replica list",
        });
      }
      setReplicas([]);
    }
  }

  async function fetchAndReturnReplicas(): Promise<string[]> {
    try {
      setIsLoading(true);
      const url = `${remoteHost}/replicas/org/${org}/gvc/${gvc}/workload/${workload.name}`;
      const res = await request({ service: "self", url: url });
      setIsLoading(false);
      return res.data.items;
    } catch (e) {
      setIsLoading(false);
      return [];
    }
  }

  // Socket and Terminal Handling Functions

  function setSocketListeners() {
    if (!socketRef.current) {
      return;
    }

    let replicaToUse = replica;
    if (!replicaToUse && !!lastUsedReplica && replicas.includes(lastUsedReplica)) {
      replicaToUse = lastUsedReplica;
    }
    if (!replicaToUse) {
      replicaToUse = replicas[0];
    }
    setLastUsedReplica(replicaToUse);

    socketRef.current.onopen = async () => {
      window.location.hash = "#socket=opened";
      const accessToken = await getToken();

      // Generate a new terminal instance
      terminalRef.current = getTerminal();
      setCounter();

      const dimensions = getDimensions();

      // Initialize request object
      const requestObj: any = {
        token: accessToken,
        org,
        gvc,
        pod: replicaToUse,
        container: containerInput,
        shell: command,
        width: dimensions.cols,
        height: dimensions.rows,
        requestVersion: "v1",
      };

      // Initiate connection
      socketRef.current!.send(JSON.stringify(requestObj));

      // Mount xTerm
      terminalRef.current.open(document.getElementById("terminal")!);
      setIsConnecting(true);
      setIsTerminalOpen(true);
      setTimeout(() => {
        terminalRef.current.focus();
      }, 100);
      incrementDebouncedCounter();

      // Feed input from xTerm to socket
      terminalRef.current.onData((val) => {
        if (socketRef.current) {
          const message: SocketMessage = {
            type: "data",
            buffer: Array.from(Buffer.from(val)),
            width: terminalRef.current.cols,
            height: terminalRef.current.rows,
          };

          socketRef.current.send(JSON.stringify(message));
        }
      });
    };

    socketRef.current.onmessage = async (event) => {
      setIsConnecting(false);

      try {
        let msg = event.data;

        // Feed socket response to xTerm
        const arrayBuffer = await (msg as Blob).arrayBuffer();
        var string = new TextDecoder().decode(arrayBuffer);
        if (terminalRef.current) {
          terminalRef.current.write(string);
        }
      } catch (e) {
        console.error("onmessage error", e.message);
      }
    };

    socketRef.current.onclose = () => {
      window.location.hash = "#socket=closed";
      setIsConnecting(false);
      socketOnClose(false);
    };

    socketRef.current.onerror = () => {
      setIsConnecting(false);
      socketOnClose(false);

      notification.error({
        message: `Unable to reach remote endpoint ${remoteHost}. Please ensure that this address is not blocked by your firewall or contact support for assistance.`,
      });
    };
  }

  function clearSocketListeners() {
    if (!socketRef.current) {
      return;
    }

    socketRef.current.onopen = () => {};
    socketRef.current.onmessage = () => {};
    socketRef.current.onclose = () => {};
    socketRef.current.onerror = () => {};
  }

  function socketOnClose(forced: boolean) {
    if (forced) {
      if (terminalRef.current) {
        terminalRef.current.dispose();
        setIsTerminalOpen(false);
      }
      terminalRef.current = null as any;
      const terminalElement = document.getElementById("terminal");
      if (terminalElement) {
        const child = terminalElement.firstChild;
        if (child) {
          terminalElement.removeChild(child);
        }
      }
    }
    clearSocketListeners();
    socketRef.current = null;
    setCounter();
    setDisconnected(!forced);
  }

  function forceCloseConnection() {
    socketOnClose(true);
    setCounter();
  }

  async function onConnect() {
    setDisconnected(false);
    socketOnClose(true);
    const client = new WebSocket(`${remoteHost.replace("https://", "wss://")}/remote`);
    socketRef.current = client;
    setSocketListeners();
  }

  async function onReconnect() {
    try {
      const _replicas = await fetchAndReturnReplicas();
      if (!_replicas.includes(lastUsedReplica)) {
        throw new Error("failed");
      } else {
        onConnect();
      }
    } catch (e) {
      notification.warning({ message: "Failed", description: "Replica is not found." });
      forceCloseConnection();
    }
  }

  async function onDetach() {
    const token = await getToken();

    const container = containerInput;
    const location = locationInput;

    let hash = `org=${org}&gvc=${gvc}&replica=${lastUsedReplica}&remoteHost=${remoteHost}&token=${token}`;
    hash += `&command=${command}&container=${container}&location=${location}&workload=${workload.name}`;

    window.open(
      `/connect/#${encodeURI(hash)}`,
      uuidv4(),
      "left=20,top=20,width=800,height=500,toolbar=no,resizable=yes,location=no",
    );
  }

  function fit() {
    if (!terminalRef.current) {
      return;
    }

    const dimensions = getDimensions();

    if (terminalRef.current.rows !== dimensions.rows || terminalRef.current.cols !== dimensions.cols) {
      terminalRef.current.resize(dimensions.cols, dimensions.rows);

      if (socketRef.current) {
        const message: SocketMessage = {
          type: "resize",
          buffer: [],
          width: terminalRef.current.cols,
          height: terminalRef.current.rows,
        };

        socketRef.current.send(JSON.stringify(message));
      }
    }
  }

  function getDimensions() {
    const terminal = terminalRef.current;
    const currentDimensions = { cols: terminal.cols, rows: terminal.rows };

    if (!terminal.element || !terminal.element.parentElement) {
      return currentDimensions;
    }

    const core = (terminal as any)._core;

    if (core._renderService.dimensions.actualCellWidth === 0 || core._renderService.dimensions.actualCellHeight === 0) {
      return currentDimensions;
    }

    const parentElementStyle = window.getComputedStyle(terminal.element.parentElement);
    const parentElementHeight = parseInt(parentElementStyle.getPropertyValue("height"));
    const parentElementWidth = Math.max(0, parseInt(parentElementStyle.getPropertyValue("width")));
    const elementStyle = window.getComputedStyle(terminal.element);
    const elementPadding = {
      top: parseInt(elementStyle.getPropertyValue("padding-top")),
      bottom: parseInt(elementStyle.getPropertyValue("padding-bottom")),
      right: parseInt(elementStyle.getPropertyValue("padding-right")),
      left: parseInt(elementStyle.getPropertyValue("padding-left")),
    };
    const elementPaddingVer = elementPadding.top + elementPadding.bottom;
    const elementPaddingHor = elementPadding.right + elementPadding.left;
    const availableHeight = parentElementHeight - elementPaddingVer;
    const availableWidth = parentElementWidth - elementPaddingHor - core.viewport.scrollBarWidth;
    const geometry = {
      cols: Math.max(2, Math.floor(availableWidth / core._renderService.dimensions.actualCellWidth)),
      rows: Math.max(2, Math.floor(availableHeight / core._renderService.dimensions.actualCellHeight)),
    };
    return geometry;
  }

  function handleCollapseToggle() {
    const _value = !isConnectHelpOpen;
    setIsConnectHelpOpen(!isConnectHelpOpen);
    localStorage.setItem(STORAGE_KEY_SHOW_CONNECT_COMMAND, _value ? "true" : "false");
  }

  function handlePortForwardCollapseToggle() {
    const _value = !isPortForwardHelpOpen;
    setIsPortForwardHelpOpen(!isPortForwardHelpOpen);
    localStorage.setItem(STORAGE_KEY_SHOW_PORT_FORWARD_COMMAND, _value ? "true" : "false");
  }

  const canConnect = !!locationInput && !!containerInput && !!remoteHost && !!command;

  const cliConnectCommand = `cpln workload connect ${workload.name} --location ${locationInput} ${
    replica && `--replica ${replica} `
  }--container ${containerInput} --shell ${command} --org ${org} --gvc ${gvc}`;
  const cliConnectDescription = (
    <p>
      Connect via terminal using the{" "}
      <a href={`${DOCS_URL}/reference/cli#workload-connect`} target="_blank" className="color-link">
        Workload Connect
      </a>{" "}
      CLI command:
    </p>
  );

  const ports = React.useMemo(() => {
    let values: number[] = [];
    for (const c of workload.spec.containers) {
      if (c.port) {
        if (!values.includes(c.port)) {
          values.push(c.port);
        }
      }
      for (const p of c.ports || []) {
        if (!values.includes(p.number)) {
          values.push(p.number);
        }
      }
    }
    let result = ``;
    for (const indexStr in values) {
      const index = Number(indexStr);
      const port = values[index];
      result += `${index === 0 ? "" : " "}${port}:${port}`;
    }
    return result;
  }, [workload.version]);

  const cliPortForwardCommand = `cpln port-forward ${workload.name} --location ${locationInput} ${
    replica && `--replica ${replica} `
  } --org ${org} --gvc ${gvc} ${ports || `{localPort}:{remotePort}`} `;
  const cliPortForwardDescription = (
    <p>
      Port forward via terminal using the{" "}
      <a href={`${DOCS_URL}/guides/cli/cpln-port-forward#cpln-port-forward`} target="_blank" className="color-link">
        Port Forward
      </a>{" "}
      CLI command:
    </p>
  );

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <div className="text-2xl">Connect</div>
        {socketRef.current && !disconnected ? (
          <Button size={"small"} type={"primary"} onClick={onDetach}>
            Detach
          </Button>
        ) : null}
        {disconnected ? <Alert message={"Disconnected"} /> : null}
      </div>
      <div className="flex justify-end mb-1">
        <button
          type={"button"}
          onClick={handlePortForwardCollapseToggle}
          className="flex items-center ngfocus color-link"
        >
          <span>{isPortForwardHelpOpen ? "Hide Port-Forward Command" : "Show Port-Forward Command"}</span>
          {isPortForwardHelpOpen ? <ChevronUp className="feather-icon" /> : <ChevronDown className="feather-icon" />}
        </button>
      </div>
      {isPortForwardHelpOpen ? (
        <div className={`mb-4 p-4 border`}>
          {cliPortForwardDescription}
          <CodeSnippet code={cliPortForwardCommand} className="mt-3 text-sm" />
        </div>
      ) : null}
      {canConnect ? (
        <>
          <div className="flex justify-end mb-1">
            <button type={"button"} onClick={handleCollapseToggle} className="flex items-center ngfocus color-link">
              <span>{isConnectHelpOpen ? "Hide Terminal Command" : "Show Terminal Command"}</span>
              {isConnectHelpOpen ? <ChevronUp className="feather-icon" /> : <ChevronDown className="feather-icon" />}
            </button>
          </div>
          {isConnectHelpOpen ? (
            <div className={`mb-4 p-4 border`}>
              {cliConnectDescription}
              <CodeSnippet code={cliConnectCommand} className="mt-3 text-sm" />
            </div>
          ) : null}
        </>
      ) : null}
      <div className="flex items-center gap-4 mb-4">
        <div>
          <div>Location</div>
          {locationFetchFailed && gvcLocations.length === 0 ? (
            <Input
              style={{ width: 450 }}
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              placeholder="Enter a location"
            />
          ) : (
            <Select
              style={{ width: 450 }}
              value={locationInput}
              onChange={setLocationInput}
              options={locationOptions}
            />
          )}
        </div>
        <div>
          <div>Container</div>
          <Select
            style={{ width: 450 }}
            value={containerInput}
            onChange={setContainerInput}
            options={containerOptions}
          />
        </div>
      </div>
      {replicas.length > 0 ? (
        <div className="flex items-center gap-4 mb-4">
          <div>
            <div>Replica</div>
            <Space.Compact style={{ width: 450 }}>
              <Select
                style={{ flex: 1 }}
                value={replica}
                onChange={(val: any) => setReplica(val)}
                options={[
                  { value: "", label: "Auto", isLabel: false, disabled: false },
                  ...replicas.map((_replica) => ({
                    label: _replica,
                    value: _replica,
                    isLabel: false,
                    disabled: false,
                  })),
                ]}
              />
              <Tooltip title="Refresh">
                <Button
                  icon={<SyncOutlined />}
                  onClick={() => fetchReplicas()}
                  loading={isLoading}
                  disabled={isLoading}
                />
              </Tooltip>
            </Space.Compact>
          </div>
          <div>
            <div>Command</div>
            <Input style={{ width: 450 }} value={command} onChange={(e) => setCommand(e.target.value)} />
          </div>
          <div>
            <div className="invisible">Connect</div>
            <Button
              type={socketRef.current ? "default" : "primary"}
              danger={!!socketRef.current}
              disabled={isLoading || !canConnect}
              loading={isLoading}
              onClick={socketRef.current ? forceCloseConnection : disconnected ? onReconnect : onConnect}
            >
              {socketRef.current ? "Disconnect" : disconnected ? "Reconnect" : "Connect"}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ width: 450 }} className="mb-4 flex gap-4 items-center">
          {isLoading ? (
            <Alert type="info" className="flex-grow" message="Fetching replicas." />
          ) : (
            <Alert type="warning" className="flex-grow" message="No replica was found." />
          )}
          <Button loading={isLoading} disabled={isLoading} type={"default"} onClick={() => fetchReplicas()}>
            Refresh
          </Button>
        </div>
      )}
      <div
        id="terminal-parent"
        className={`relative p-4 bg-black ${disconnected ? "" : "resize overflow-scroll"} ${
          isTerminalOpen ? "visible" : "invisible"
        }`}
      >
        <div id={`terminal`} className="w-full h-full bg-black" />
        {isTerminalOpen && isConnecting ? (
          <div
            className="absolute p-2 rounded bg-dark text-white"
            style={{ left: "50%", top: "50%", zIndex: 10, transform: "translateX(-50%) translateY(-50%)" }}
          >
            <Loader className="feather-icon animate-spin" />
          </div>
        ) : null}
      </div>
      <div className="absolute invisible" style={{ left: -999 }}>
        {counter}
      </div>
    </div>
  );
};
